/**
 * Cart drawer "Pairs Well With" recommendations.
 * Prefetches as early as possible so results are ready when the drawer opens.
 */
(function () {
  const SECTION_ID = 'api-cart-recommendations';
  const LIMIT = 3;
  const cache = new Map(); // productId -> html
  let loadToken = 0;
  let inflight = new Map(); // productId -> Promise
  let observer = null;
  let lastProductId = '';

  function getHolder() {
    return document.querySelector('[data-cart-recommendations]');
  }

  function getProductIdFromHolder(holder) {
    return holder?.getAttribute('data-product-id') || '';
  }

  function buildUrl(productId, intent) {
    const base = window.theme?.routes?.product_recommendations_url;
    if (!base || !productId) return null;

    const url = new URL(base, window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', String(LIMIT));
    if (intent) url.searchParams.set('intent', intent);
    return url.toString();
  }

  async function fetchRecommendationsHtml(productId, intent) {
    const url = buildUrl(productId, intent);
    if (!url) return '';

    const response = await fetch(url);
    if (!response.ok) return '';

    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const section =
      doc.querySelector('[data-api-cart-recommendations]') ||
      doc.querySelector(`#shopify-section-${SECTION_ID}`) ||
      doc.getElementById(`shopify-section-${SECTION_ID}`);

    if (!section) return '';

    const source = section.matches('[data-api-cart-recommendations]')
      ? section
      : section.querySelector('[data-api-cart-recommendations]') || section;

    return source.innerHTML.trim();
  }

  function hasCards(html) {
    return Boolean(html && html.includes('cart-pairs__card'));
  }

  async function fetchBestRecommendations(productId) {
    if (cache.has(productId)) return cache.get(productId);
    if (inflight.has(productId)) return inflight.get(productId);

    const promise = (async () => {
      // Fire complementary + related in parallel — first with cards wins
      const intents = ['complementary', 'related', ''];
      const results = await Promise.all(
        intents.map((intent) =>
          fetchRecommendationsHtml(productId, intent).catch(() => '')
        )
      );

      const html = results.find(hasCards) || '';
      if (hasCards(html)) cache.set(productId, html);
      return html;
    })();

    inflight.set(productId, promise);
    try {
      return await promise;
    } finally {
      inflight.delete(productId);
    }
  }

  function renderIntoHolder(holder, productId, html) {
    if (!holder) return;

    if (hasCards(html)) {
      holder.innerHTML = html;
      holder.classList.add('is-loaded');
      holder.classList.remove('is-loading');
      holder.setAttribute('data-loaded-product-id', productId);
      document.dispatchEvent(
        new CustomEvent('theme:cart:recommendations:loaded', { bubbles: true })
      );
    } else {
      holder.innerHTML = '';
      holder.classList.remove('is-loaded', 'is-loading');
      holder.removeAttribute('data-loaded-product-id');
    }
  }

  async function loadCartRecommendations(productIdOverride) {
    const holder = getHolder();
    const productId = productIdOverride || getProductIdFromHolder(holder);

    if (!productId) {
      if (holder) {
        holder.innerHTML = '';
        holder.classList.remove('is-loaded', 'is-loading');
        holder.removeAttribute('data-loaded-product-id');
      }
      return;
    }

    lastProductId = productId;

    // Instant path: already rendered for this product
    if (
      holder &&
      holder.classList.contains('is-loaded') &&
      holder.getAttribute('data-loaded-product-id') === productId &&
      holder.querySelector('.cart-pairs__card')
    ) {
      return;
    }

    // Instant path: serve cache
    if (holder && cache.has(productId)) {
      renderIntoHolder(holder, productId, cache.get(productId));
      return;
    }

    const token = ++loadToken;
    holder?.classList.add('is-loading');
    holder?.classList.remove('is-loaded');

    try {
      const html = await fetchBestRecommendations(productId);
      if (token !== loadToken) return;

      const currentHolder = getHolder();
      // Holder may not exist yet (drawer template not mounted) — cache is enough
      if (currentHolder && getProductIdFromHolder(currentHolder) === productId) {
        renderIntoHolder(currentHolder, productId, html);
      } else if (currentHolder && !getProductIdFromHolder(currentHolder) && productId) {
        currentHolder.setAttribute('data-product-id', productId);
        renderIntoHolder(currentHolder, productId, html);
      }
    } catch (error) {
      console.error('Cart recommendations failed', error);
      if (token !== loadToken) return;
      const currentHolder = getHolder();
      if (currentHolder) {
        currentHolder.innerHTML = '';
        currentHolder.classList.remove('is-loaded', 'is-loading');
        currentHolder.removeAttribute('data-loaded-product-id');
      }
    }
  }

  async function prefetchFromCartJs() {
    try {
      const root = window.theme?.routes?.root || '/';
      const cartUrl = root.endsWith('/') ? `${root}cart.js` : `${root}/cart.js`;
      const response = await fetch(cartUrl, { credentials: 'same-origin' });
      if (!response.ok) return;

      const cart = await response.json();
      const productId = cart?.items?.[cart.items.length - 1]?.product_id;
      if (productId) {
        lastProductId = String(productId);
        await fetchBestRecommendations(String(productId));
      }
    } catch (error) {
      // Silent — recommendations will load on drawer open
    }
  }

  function onDrawerInteraction() {
    const holder = getHolder();
    const productId = getProductIdFromHolder(holder) || lastProductId;
    loadCartRecommendations(productId);
  }

  function observeCartDrawer() {
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    if (!cartDrawer || observer) return;

    observer = new MutationObserver(() => {
      const holder = getHolder();
      if (!holder) return;

      const productId = getProductIdFromHolder(holder) || lastProductId;
      if (!productId) return;

      if (cache.has(productId) && !holder.querySelector('.cart-pairs__card')) {
        renderIntoHolder(holder, productId, cache.get(productId));
        return;
      }

      if (!holder.querySelector('.cart-pairs__card') && !holder.classList.contains('is-loading')) {
        loadCartRecommendations(productId);
      }
    });

    observer.observe(cartDrawer, { childList: true, subtree: true });
  }

  // Prefetch on first cart icon hover/focus for instant open
  function bindEagerTriggers() {
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((toggle) => {
      const prefetch = () => {
        const holder = getHolder();
        const productId = getProductIdFromHolder(holder) || lastProductId;
        if (productId) fetchBestRecommendations(productId);
        else prefetchFromCartJs();
      };

      toggle.addEventListener('pointerenter', prefetch, { once: true });
      toggle.addEventListener('focus', prefetch, { once: true });
      toggle.addEventListener('click', onDrawerInteraction);
    });
  }

  document.addEventListener('theme:cart:loaded', onDrawerInteraction);
  document.addEventListener('theme:cart:added', () => {
    cache.clear();
    onDrawerInteraction();
    prefetchFromCartJs();
  });
  document.addEventListener('theme:cart:open', onDrawerInteraction);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      observeCartDrawer();
      bindEagerTriggers();
      prefetchFromCartJs();
    });
  } else {
    observeCartDrawer();
    bindEagerTriggers();
    prefetchFromCartJs();
  }
})();
